import { createTheme, Theme } from "@mui/material/styles";
import type { ThemeMode } from "./types";

/* =========================
   SHARED: Typography, shape, component overrides
   ========================= */
const typography = {
  fontFamily: "'Inter', sans-serif",

  /* 🔹 HERO / LANDING (rare) */
  h1: {
    fontSize: "28px",
    fontWeight: 700,
    lineHeight: 1.3,
  },

  /* 🔹 PAGE TITLES */
  h2: {
    fontSize: "24px",
    fontWeight: 600,
    lineHeight: 1.35,
  },

  /* 🔹 SECTION TITLES */
  h3: {
    fontSize: "20px",
    fontWeight: 800,
    lineHeight: 1.4,
  },

  /* 🔹 APP TITLE (Budget-Trace) */
  h5: {
    fontSize: "18px",
    fontWeight: 600,
    lineHeight: 1.4,
  },

  /* 🔹 SMALL HEADERS */
  h6: {
    fontSize: "16px",
    fontWeight: 600,
    lineHeight: 1.4,
  },

  /* 🔹 MAIN BODY TEXT */
  body1: {
    fontSize: "14px",
    fontWeight: 400,
    lineHeight: 1.6,
  },

  /* 🔹 SECONDARY TEXT */
  body2: {
    fontSize: "13px",
    fontWeight: 400,
    lineHeight: 1.5,
    color: "#6B7280",
  },

  /* 🔹 CAPTIONS / HINTS */
  caption: {
    fontSize: "12px",
    fontWeight: 400,
    lineHeight: 1.4,
    color: "#9CA3AF",
  },
};

const shape = { borderRadius: 12 };

const lightPalette = {
  primary: { main: "#5290FF" },
  background: { default: "#F9FAFB", paper: "#FFFFFF" },
  text: { primary: "#111827", secondary: "#6B7280" },
  divider: "#E5E7EB",
};

const darkPalette = {
  primary: { main: "#5290FF" },
  background: { default: "#121212", paper: "#1E1E1E" },
  text: { primary: "#E5E7EB", secondary: "#9CA3AF" },
  divider: "#374151",
};

/**
 * Returns MUI theme for the given mode. Keeps design rules (typography, shape, components);
 * only palette changes between light and dark.
 */
export function getTheme(mode: ThemeMode): Theme {
  const palette = mode === "dark" ? darkPalette : lightPalette;

  return createTheme({
    palette: {
      mode,
      ...palette,
    },
    typography: {
      ...typography,
      body2: { ...(typography.body2 as object), color: palette.text.secondary },
      caption: { ...(typography.caption as object), color: palette.text.secondary },
    },
    shape,
    components: {
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 500,
            padding: "10px 16px",
            borderRadius: 10,
          },
        },
      },
      MuiTextField: {
        defaultProps: { variant: "outlined", size: "medium" },
      },
      MuiOutlinedInput: {
        styleOverrides: { root: { borderRadius: 10 } },
      },
      MuiPaper: {
        styleOverrides: { root: { borderRadius: 16 } },
      },
      MuiDivider: {
        styleOverrides: { root: { borderColor: palette.divider } },
      },
    },
  });
}

const theme = getTheme("light");
export default theme;
